import React, { useState, useEffect } from 'react';
import { instrumentService } from '../../../services/api/instrumentService';
import { productService } from '../../../services/api/productService';
import type { MarketInstrument, MarketType } from '../../../types/instrument';
import { ASSET_CLASS_MAP } from '../../../types/instrument';
import './MarketSelectionModal.css';

interface MarketSelectionModalProps {
  isOpen: boolean;
  isMobile: boolean;
  selectedMarketType: string;
  selectedProduct: string;
  searchQuery: string;
  selectedInstrument: string;
  onClose: () => void;
  onMarketTypeSelect: (market: string) => void;
  onSearchChange: (query: string) => void;
  onInstrumentSelect: (instrument: { id?: string; market: string; symbol: string; name: string; price: string; change: string }) => void;
}

export const MarketSelectionModal: React.FC<MarketSelectionModalProps> = ({
  isOpen,
  isMobile,
  selectedMarketType,
  selectedProduct,
  searchQuery,
  selectedInstrument,
  onClose,
  onMarketTypeSelect,
  onSearchChange,
  onInstrumentSelect
}) => {
  const [instruments, setInstruments] = useState<MarketInstrument[]>([]);
  const [productFilteredInstruments, setProductFilteredInstruments] = useState<MarketInstrument[]>([]);
  const [availableMarketTypes, setAvailableMarketTypes] = useState<string[]>(['Forex', 'Indices', 'Crypto', 'Commodities', 'Synthetic']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load available market types when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    loadAvailableMarketTypes();
  }, [isOpen]);

  // Load instruments when market type or product changes or modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    loadInstruments();
  }, [isOpen, selectedMarketType, selectedProduct]);

  // Filter instruments by product compatibility when instruments or product changes
  useEffect(() => {
    filterInstrumentsByProduct();
  }, [instruments, selectedProduct]);

  // Load available market types from API
  const loadAvailableMarketTypes = async () => {
    try {
      const marketTypes = await instrumentService.getAvailableMarketTypes();
      setAvailableMarketTypes(marketTypes);
    } catch (error) {
      console.error('Failed to load available market types:', error);
      // Keep default fallback values
    }
  };

  const loadInstruments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const assetClass = ASSET_CLASS_MAP[selectedMarketType as MarketType];
      if (!assetClass) {
        throw new Error(`Invalid market type: ${selectedMarketType}`);
      }
      
      const marketInstruments = await instrumentService.getInstrumentsByAssetClass(assetClass);
      setInstruments(marketInstruments);
    } catch (err) {
      console.error('Failed to load instruments:', err);
      setError('Failed to load instruments. Please try again.');
      
      // No fallback - rely entirely on API
      setInstruments([]);
    } finally {
      setLoading(false);
    }
  };

  const filterInstrumentsByProduct = async () => {
    if (instruments.length === 0) {
      setProductFilteredInstruments([]);
      return;
    }

    try {
      // Filter instruments based on product compatibility
      const filtered = await productService.filterInstrumentsByProduct(selectedProduct, instruments);
      setProductFilteredInstruments(filtered);
    } catch (error) {
      console.error('Failed to filter instruments by product:', error);
      // On error, show all instruments to avoid breaking the UI
      setProductFilteredInstruments(instruments);
    }
  };


  const handleInstrumentClick = (instrument: MarketInstrument) => {
    // Convert MarketInstrument to the expected format
    const formattedInstrument = {
      id: instrument.instrumentId,
      market: instrument.market,
      symbol: instrument.symbol,
      name: instrument.name,
      price: instrument.price,
      change: instrument.change
    };
    
    onInstrumentSelect(formattedInstrument);
    onClose();
  };

  const handleSearch = async (query: string) => {
    onSearchChange(query);
    
    if (!query.trim()) {
      // If search is cleared, reload all instruments for current market
      loadInstruments();
      return;
    }

    setLoading(true);
    try {
      const assetClass = ASSET_CLASS_MAP[selectedMarketType as MarketType];
      const searchResults = await instrumentService.searchInstruments(query, assetClass);
      setInstruments(searchResults);
    } catch (err) {
      console.error('Search failed:', err);
      // Filter current instruments locally as fallback
      const filtered = instruments.filter(instrument =>
        instrument.symbol.toLowerCase().includes(query.toLowerCase())
      );
      setInstruments(filtered);
    } finally {
      setLoading(false);
    }
  };

  const filteredInstruments = searchQuery 
    ? productFilteredInstruments.filter(instrument =>
        instrument.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        instrument.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : productFilteredInstruments;

  if (!isOpen) return null;

  return (
    <div className="market-modal-overlay" onClick={onClose}>
      <div className={`market-modal ${isMobile ? 'mobile-market-modal' : 'desktop-market-modal'}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Market & Instrument</h3>
          <button 
            className="modal-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="modal-content">
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              className="market-modal-search"
              placeholder="Search instruments..."
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <div className="market-types">
            {availableMarketTypes.map(market => (
              <button
                key={market}
                className={`market-type-btn ${selectedMarketType === market ? 'active' : ''}`}
                onClick={() => onMarketTypeSelect(market)}
              >
                {market}
              </button>
            ))}
          </div>
          <div className="instruments-list">
            {loading ? (
              <div style={{ color: '#888', padding: '12px 0', textAlign: 'center' }}>
                Loading instruments...
              </div>
            ) : error ? (
              <div style={{ color: '#ff6b6b', padding: '12px 0', textAlign: 'center' }}>
                {error}
                <button 
                  onClick={loadInstruments}
                  style={{ 
                    display: 'block', 
                    margin: '8px auto 0', 
                    padding: '4px 12px',
                    background: '#00d4ff',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            ) : filteredInstruments.length === 0 ? (
              <div style={{ color: '#888', padding: '12px 0', textAlign: 'center' }}>
                {searchQuery ? 'No instruments found for your search.' : 'No instruments available.'}
              </div>
            ) : (
              filteredInstruments.map(instrument => {
                const isActive = instrument.instrumentId === selectedInstrument;
                return (
                  <div 
                    className={`instrument-item${isActive ? ' active' : ''}`} 
                    key={instrument.instrumentId || instrument.symbol} 
                    style={{
                      border: isActive ? '2px solid #00d4ff' : '1px solid #222',
                      background: isActive ? '#1a2b3a' : undefined,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleInstrumentClick(instrument)}
                  >
                    <span className="symbol">{instrument.name}</span>
                    <span className="price">
                      {instrument.price} 
                      <span className={`change ${instrument.change.startsWith('+') ? 'positive' : 'negative'}`}>
                        {instrument.change}
                      </span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
